import { ProfileBadgesQuery } from 'graphql/__generated__/ProfileBadgesQuery.graphql';
import { PROFILE_BADGES_QUERY } from 'graphql/ProfileBadgesQuery';
import { useLazyLoadQuery } from 'react-relay';
import { MostRecentAvatars } from 'components/common/most-recent-avatars/MostRecentAvatars';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import {
  BadgesEdge,
  BadgesEdges,
  UnifiedProfileForBadges,
} from 'constants/types';
import MainPanelSectionHeader from 'components/common/section/MainPanelSectionHeader';
import ElevatedContainer from 'components/common/section/ElevatedContainer';
import SubSection from 'components/common/section/sub-section/SubSection';
import useIsPersonalView from 'utils/useIsPersonalView';
import EmptyStateMessageBody from 'components/common/section/sub-section/EmptyStateMessageBody';
import { AvatarData } from 'components/common/most-recent-avatars/MostRecentAvatarTypes';
import Fallback from 'components/common/fallback/Fallback';
import { logBoundaryError } from 'services/logger';
import { ErrorBoundary } from 'react-error-boundary';
import { LEARNING_BADGES_DEFAULT_URL, SCREEN_NAME } from 'constants/index';
import { sendUIEvent } from 'services/analytics';
import SectionSummaryContainer from 'components/common/section/SectionSummaryContainer';
import { LoadingMainPanelSection } from 'components/common/suspense-fallbacks/LoadingMainPanelSection';
import { Suspense } from 'react';
import { SectionFeedback } from 'components/errors/SectionFeedback';

export const BadgesSummaryWrapper = ({
  internalId,
  profileHasConsented,
}: {
  internalId: string;
  profileHasConsented: boolean;
}) => (
  <ErrorBoundary FallbackComponent={Fallback} onError={logBoundaryError}>
    <Suspense fallback={<LoadingMainPanelSection hasHeader={true} />}>
      <BadgesSummary
        internalId={internalId}
        profileHasConsented={profileHasConsented}
      />
    </Suspense>
  </ErrorBoundary>
);

const BadgesSummary = ({
  internalId,
  profileHasConsented,
}: {
  internalId: string;
  profileHasConsented: boolean;
}) => {
  const isPersonalView = useIsPersonalView(internalId);

  const badgesResponse = useLazyLoadQuery<ProfileBadgesQuery>(
    PROFILE_BADGES_QUERY,
    { internalId },
  ).unified?.unifiedProfile as UnifiedProfileForBadges;
  const forumsBadgesEdges =
    (badgesResponse?.badges?.edges as BadgesEdges) || [];
  const recentCoursesBadgesEdges =
    (badgesResponse?.learning?.recentCoursesBadges?.edges as BadgesEdges) || [];

  const forumsBadges = forumsBadgesEdges.map((edge: BadgesEdge) => ({
    ...(edge?.node ?? {}),
    src: edge?.node?.imageUrl,
  }));
  const recentCoursesBadges = recentCoursesBadgesEdges.map(
    (edge: BadgesEdge) => ({
      ...(edge?.node ?? {}),
      src: edge?.node?.imageUrl,
    }),
  );

  const totalForumsBadges = badgesResponse?.badges?.totalCount ?? 0;
  const totalLearningBadges =
    badgesResponse?.learning?.recentCoursesBadges?.totalCount ?? 0;
  const totalNumberOfBadges = totalForumsBadges + totalLearningBadges;
  const titleParts = [totalNumberOfBadges || ''];
  titleParts.push(totalNumberOfBadges === 1 ? 'Badge' : 'Badges');

  // ideally this scenario should never happen since one badge always exist, but this behavior has been seen due to bunchball connection issue
  if (!isPersonalView && !totalNumberOfBadges) {
    return null;
  }

  return (
    <SectionSummaryContainer>
      <MainPanelSectionHeader
        testId="badges-summary-title"
        title={titleParts.join(' ')}
        link={`/profile/${internalId}/badges`}
        linkText={totalNumberOfBadges ? 'View all' : undefined}
        subtitle={
          isPersonalView ? (
            <span>
              {'Earn badges for your activity and learning achievements.'}
            </span>
          ) : undefined
        }
        viewAllAnalyticsName="viewAllBadges"
      />
      <ElevatedContainer>
        <div css={avatarContainerStyles}>
          <MostRecentCourses
            isPersonalView={isPersonalView}
            recentCoursesBadges={recentCoursesBadges}
            profileHasConsented={profileHasConsented}
          />
          <MostRecentBadges
            isPersonalView={isPersonalView}
            recentBadges={forumsBadges}
          />
        </div>
      </ElevatedContainer>
    </SectionSummaryContainer>
  );
};
export default BadgesSummary;

const MostRecentCourses = ({
  isPersonalView,
  recentCoursesBadges,
  profileHasConsented,
}: {
  isPersonalView: boolean;
  recentCoursesBadges: AvatarData[];
  profileHasConsented: boolean;
}) => {
  const isRecentCoursesZero =
    recentCoursesBadges.length === 0 || !profileHasConsented;

  const sendStartLearningAnalytics = () => {
    sendUIEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'coursesStartLearning',
      source: SCREEN_NAME.PROFILE,
      isPersonalView,
    });
  };

  if (!isPersonalView && isRecentCoursesZero) {
    return null;
  }
  // https://learning.atlassian.com/catalog does not exist yet
  return (
    <div
      data-testid="most-recent-courses-container"
      css={[
        boxStyles,
        recentCoursesStyle,
        isRecentCoursesZero && emptyCoursesStyles,
      ]}
    >
      <SubSection title={'MOST RECENT COURSES'}>
        {isRecentCoursesZero ? (
          <EmptyStateMessageBody
            message="Complete a course to earn your first learning badge."
            linkTo={LEARNING_BADGES_DEFAULT_URL}
            linkType="external"
            sendAnalyticsEvent={sendStartLearningAnalytics}
            linkText="Start learning"
          />
        ) : (
          <MostRecentAvatars data={recentCoursesBadges} />
        )}
      </SubSection>
    </div>
  );
};

const MostRecentBadges = ({
  isPersonalView,
  recentBadges,
}: {
  isPersonalView: boolean;
  recentBadges: AvatarData[];
}) => {
  const isRecentBadgesZero = recentBadges.length === 0;

  //error state
  if (!isPersonalView && isRecentBadgesZero) {
    return null;
  }
  return (
    <div
      data-testid="most-recent-badges-container"
      css={[boxStyles, badgesStyle]}
    >
      <SubSection title={'MOST RECENT BADGES'}>
        {isRecentBadgesZero ? (
          <SectionFeedback description="This section failed to load"></SectionFeedback>
        ) : (
          <MostRecentAvatars data={recentBadges} />
        )}
      </SubSection>
    </div>
  );
};

const avatarContainerStyles = css({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  gap: token('space.600'),
  '@media (max-width: 767px)': {
    flexDirection: 'column',
    gap: token('space.300'),
  },
});

const boxStyles = css({
  width: '50%',
  borderRadius: token('border.radius'),
  borderWidth: token('border.width'),
  '@media (max-width: 768px)': {
    width: '100%',
  },
});

const recentCoursesStyle = css({
  order: 1,
});

const emptyCoursesStyles = css({
  order: 3,
});

const badgesStyle = css({
  order: 2,
});
